/**
 * Trigger + poll for meeting-room calendar events on the ATS backend.
 *
 * Same two endpoints as interviewer busy (`/calendar_events/trigger_fetch`
 * → `/calendar_events`) — the difference is that we send `room_emails`
 * (strings) instead of `user_ids` (numbers) and the poll envelope
 * reports results under `room_events` keyed by `room_email`.
 *
 * The function is deliberately small and framework-free so React
 * Query can wrap it without leaking implementation details.
 */
import { api } from '@/api/request';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import type {
  MeetingRoomBusyEvent,
  MeetingRoomBusyFetchResult,
  MeetingRoomInfo,
} from './types';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15;
const ALLOWED_STATUSES = new Set(['busy', 'oof', 'unknown']);

export interface FetchMeetingRoomBusyArgs {
  roomEmails: string[];
  /** ISO window start (inclusive). */
  fromIso: string;
  /** ISO window end (exclusive). */
  toIso: string;
  timezone: string;
  atsJobId?: number;
  signal?: AbortSignal;
}

interface PollPayload {
  status?: 'PENDING' | 'SUCCESS' | 'FAILED';
  results?: {
    room_events?: Record<string, Array<Record<string, unknown>>>;
    rooms_info?: MeetingRoomInfo[];
  };
}

export async function fetchMeetingRoomBusyEvents(
  args: FetchMeetingRoomBusyArgs
): Promise<MeetingRoomBusyFetchResult> {
  if (args.roomEmails.length === 0) {
    return { events: [], roomsInfo: [] };
  }
  const tz = normalizeTimeZoneForAtsCalendarApi(args.timezone);

  const body: Record<string, unknown> = {
    from: args.fromIso,
    to: args.toIso,
    time_zone: tz,
    user_ids: [],
    room_emails: args.roomEmails,
  };
  if (args.atsJobId !== undefined && args.atsJobId > 0) {
    body.ats_job_id = args.atsJobId;
  }

  const trigger = await api.post<{ fetch_uuid?: string }>(
    'calendar_events/trigger_fetch',
    body,
    { signal: args.signal }
  );
  const fetchUuid = trigger.fetch_uuid;
  if (!fetchUuid) {
    throw new Error('meeting_room_trigger_missing_fetch_uuid');
  }

  // Poll until SUCCESS or FAILED (or we hit MAX_POLLS).
  for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
    if (args.signal?.aborted) {
      throw new DOMException('aborted', 'AbortError');
    }
    const pollParams: Record<string, string | number> = {
      fetch_uuid: fetchUuid,
    };
    if (args.atsJobId !== undefined && args.atsJobId > 0) {
      pollParams.ats_job_id = args.atsJobId;
    }
    const poll = await api.get<PollPayload>('calendar_events', {
      params: pollParams,
      signal: args.signal,
    });
    if (poll.status === 'SUCCESS') {
      return parseRoomEvents(poll);
    }
    if (poll.status === 'FAILED') {
      throw new Error('meeting_room_fetch_failed');
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  throw new Error('meeting_room_fetch_timeout');
}

function parseRoomEvents(poll: PollPayload): MeetingRoomBusyFetchResult {
  const roomEvents = poll.results?.room_events ?? {};
  const roomsInfo = poll.results?.rooms_info ?? [];
  const events: MeetingRoomBusyEvent[] = [];
  for (const [email, list] of Object.entries(roomEvents)) {
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      if (!raw || typeof raw !== 'object') continue;
      const r = raw as Record<string, unknown>;
      const status = typeof r.status === 'string' ? r.status : undefined;
      if (status && !ALLOWED_STATUSES.has(status)) continue;
      const start = (r.start_time ?? r.start) as string | undefined;
      const end = (r.end_time ?? r.end) as string | undefined;
      if (!start || !end) continue;
      events.push({
        id:
          typeof r.id === 'string' || typeof r.id === 'number'
            ? String(r.id)
            : undefined,
        roomEmail: email,
        start,
        end,
        title: (r.subject ?? r.title ?? 'Busy') as string,
        status,
      });
    }
  }
  return { events, roomsInfo };
}
